import { z } from "zod";

/**
 * Modules (ADR 0017): parts of the app a garage switches on. A module is active in a garage when
 * the host offers it, the garage is entitled to it and a garage admin switched it on.
 */

export const MODULE_NAMES = ["qrLabels", "vehicleManagement"] as const;
export type ModuleName = (typeof MODULE_NAMES)[number];

/** Included: every garage is entitled. Booked: only a booking or a licence entitles one. */
export interface ModuleDefinition {
  entitlement: "included" | "booked";
}

export const MODULES: Record<ModuleName, ModuleDefinition> = {
  qrLabels: { entitlement: "included" },
  // Included while it is new; making it paid is this value plus bookings (ADR 0017).
  vehicleManagement: { entitlement: "included" },
};

/**
 * What entitles a garage to a module: always, or a booking that ends on a day (an ISO date, UTC).
 * From the day it ends on, the module is no longer active, so its writes are refused.
 */
export type Entitlement = { kind: "always" } | { kind: "booking"; endsOn: string };

export const ALWAYS: Entitlement = { kind: "always" };

/**
 * A module's entitlement: an included one always, a booked one by the host's booking or licence
 * (`booking`; null where there is none).
 */
export function entitlementOf(
  definition: ModuleDefinition,
  booking: Entitlement | null,
): Entitlement | null {
  return definition.entitlement === "included" ? ALWAYS : booking;
}

/** Entitled and switched on; `today` is the UTC date (`utcToday()`, `todayIso()`). */
export function moduleActive(
  entitlement: Entitlement | null,
  switchedOn: boolean,
  today: string,
): boolean {
  if (!switchedOn || !entitlement) return false;
  return entitlement.kind === "always" || today < entitlement.endsOn;
}

/** The `modules` garage setting: which modules a garage admin switched on. */
export type ModuleSwitches = Record<ModuleName, boolean>;

export const DEFAULT_MODULE_SWITCHES: ModuleSwitches = {
  qrLabels: false,
  vehicleManagement: false,
};

/** The switches from a stored value: unknown names dropped, missing or broken ones off. */
export function moduleSwitchesOf(value: unknown): ModuleSwitches {
  const stored = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  const switches = { ...DEFAULT_MODULE_SWITCHES };
  for (const name of MODULE_NAMES) switches[name] = stored[name] === true;
  return switches;
}

/** A change to the switches; names not given keep their state, unknown ones are dropped. */
export const moduleSwitchesInput = z.object(
  Object.fromEntries(MODULE_NAMES.map((name) => [name, z.boolean().optional()])) as Record<
    ModuleName,
    z.ZodOptional<z.ZodBoolean>
  >,
);

/**
 * The modules active in a garage: those the host offers (`offered`), entitled (`booking` gives a
 * booked module's entitlement) and switched on.
 */
export function activeModules(options: {
  offered: readonly ModuleName[];
  switches: ModuleSwitches;
  booking: (module: ModuleName) => Entitlement | null;
  today: string;
}): ModuleName[] {
  return MODULE_NAMES.filter(
    (name) =>
      options.offered.includes(name) &&
      moduleActive(
        entitlementOf(MODULES[name], options.booking(name)),
        options.switches[name],
        options.today,
      ),
  );
}
